import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { axios } from '../../api';
import { switchAccountShippingMethod } from '../../api/switchAccountShippingMethod';
import { Pencil } from '../../assets/Pencil';
import { IS_PROD } from '../../config';
import { isAdmin, isAllModuleAccessUser, isOpsAdmin, isTeamMember } from '../../pages/constants';
import {
  BOTTOM_TOAST,
  ECONOMY_SHIPPING_OPTION,
  formatPhoneNumber,
  generateErrorMsg,
  SHIPMENT_DISPLAY_VALUE,
} from '../../utils';
import { ConfirmationModal } from '../ConfirmationModal';
import { DefaultPasswordUpdate } from '../DefaultPasswordUpdate';
import { DeletePII } from '../DeleteCustomerPII';
import { OptInSMS } from '../OptInSMS';
import { SwitchPaymentProcessor } from '../SwitchPaymentProcessor';
import { TextInput } from '../TextInput';
import { Button } from '../buttons/Button';

import { Customer } from '@/pages/types';

export const AccountAndContactInfo = ({ customer }: { customer: Customer }) => {
  const [isEditingEmail, setIsEditingEmail] = useState<boolean>(false);
  const [updatedEmail, setUpdatedEmail] = useState<string>('');
  const [primaryPaymentProcessor, setPrimaryPaymentProcessor] = useState<string>(
    customer.primary_payment_processor
  );
  const [showSwitchToStandardConfirmation, setShowSwitchToStandardConfirmation] =
    useState<boolean>(false);
  const [isSwitchingShippingMethod, setIsSwitchingShippingMethod] = useState<boolean>(false);

  const showDeletePiiOption = () => {
    if (
      customer.first_name === 'DELETED' &&
      customer.last_name === 'DELETED' &&
      customer.email.includes('DELETED')
    ) {
      return false;
    }
    return true;
  };

  const showOptInSMS = !!customer.phone_number && !IS_PROD;

  const defaultShippingMethod = customer.default_shipping_method_id
    ? SHIPMENT_DISPLAY_VALUE[customer.default_shipping_method_id] ||
      customer.default_shipping_method_id
    : 'N/A';

  const handleSwitchToStandard = async () => {
    try {
      setIsSwitchingShippingMethod(true);
      await switchAccountShippingMethod(customer.email);
      toast.success('Default shipping method updated to Standard', BOTTOM_TOAST);
      setShowSwitchToStandardConfirmation(false);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (e) {
      toast.error(generateErrorMsg(e), BOTTOM_TOAST);
    } finally {
      setIsSwitchingShippingMethod(false);
    }
  };

  const shouldShowSwitchShippingOption =
    isAdmin() || isOpsAdmin() || isAllModuleAccessUser() || isTeamMember();

  return (
    <div className="flex-col">
      {showSwitchToStandardConfirmation && (
        <ConfirmationModal
          title="Are you sure you want to change this customer's default shipping method?"
          message="The customer's account level shipping method will be switched from Economy to Standard for all future orders."
          confirmLabel="Confirm"
          onConfirm={handleSwitchToStandard}
          onCancel={() => setShowSwitchToStandardConfirmation(false)}
          isProcessLoading={isSwitchingShippingMethod}
        />
      )}
      <h2 className="mb-1 flex items-center">
        <b className="inline-block">Customer Details</b>
        <div className="text-sm ml-2">
          <Link className="cursor-pointer underline text-anchor" to="changelog">
            View Changelog
          </Link>
        </div>
      </h2>
      <div className="bg-white border rounded-lg p-4 shadow-md h-[260px] overflow-y-scroll">
        <div className="mb-2">
          <strong>Customer ID:</strong> {customer.id}
        </div>
        <div className="mb-2 flex items-center">
          <strong>Email address:</strong>{' '}
          {!isEditingEmail && (
            <div className="flex items-center ml-1">
              <span>{customer.email}</span>
              {customer.status !== 'CANCELLED' && (
                <a
                  onClick={() => {
                    setIsEditingEmail(true);
                  }}
                  className="cursor-pointer underline text-anchor ml-1"
                >
                  <Pencil className="w-4 h-4 mx-1 text-black" />
                </a>
              )}
            </div>
          )}
          {isEditingEmail && (
            <div className="text-sm ml-2 flex items-center">
              <div className="w-56 h-[20px]">
                <TextInput
                  value={updatedEmail}
                  placeholder="New Email"
                  setTextChange={setUpdatedEmail}
                  className="py-0 px-2"
                />
              </div>
              <a
                onClick={() => setIsEditingEmail(false)}
                className="cursor-pointer underline text-anchor ml-2"
              >
                Cancel
              </a>{' '}
              <Button
                label="Save"
                variant="primary"
                className="ml-2 h-6 min-w-[30px]"
                onClick={() =>
                  axios
                    .put(`accounts/${customer.id}/update_customer_email`, {
                      new_email: updatedEmail,
                    })
                    .then(() => {
                      // TODO: Add refresh customer here
                      window.location.reload();
                    })
                    .catch((e) => {
                      toast.error(generateErrorMsg(e), BOTTOM_TOAST);
                    })
                }
              />
            </div>
          )}
          <DefaultPasswordUpdate email={customer.email} />
          {showDeletePiiOption() && <DeletePII email={customer.email} />}
        </div>
        <div className="mb-2 flex items-center">
          <strong className="mr-1">Phone number:</strong>
          <span className="mr-2">{formatPhoneNumber(customer.phone_number)}</span>

          {showOptInSMS && <OptInSMS customerId={customer.id} />}
        </div>
        <div className="mb-2">
          <strong>Account status:</strong> {customer.status}
        </div>
        <div className="mb-2">
          <strong>Gladly ID:</strong> {customer.gladly_customer_id || 'N/A'}
        </div>
        <div className="mb-2">
          <strong>Pricing Model:</strong> {customer.default_pricing_model?.name}
        </div>
        <div className="mb-2 flex items-center">
          <strong className="mr-1">Default shipping method:</strong>
          {defaultShippingMethod}
          {shouldShowSwitchShippingOption
            ? customer.default_shipping_method_id === ECONOMY_SHIPPING_OPTION.value && (
                <Button
                  className="px-[2px] py-[2px] ml-2 active:cursor-progress"
                  variant="secondary"
                  textClassName="text-sm font-normal"
                  label="Switch To Standard"
                  onClick={() => setShowSwitchToStandardConfirmation(true)}
                />
              )
            : null}
        </div>
        <div className="mb-2">
          <div className="mb-2 flex items-center">
            <strong>Primary Payment Processor:</strong>
            <div className="flex items-center ml-1">
              {primaryPaymentProcessor}
              <SwitchPaymentProcessor
                customer_id={customer.id}
                setPrimaryPaymentProcessor={setPrimaryPaymentProcessor}
                primaryPaymentProcessor={primaryPaymentProcessor}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
